import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, del, post } from '../lib/api.js';
import { errorMessage } from '../lib/passkey.js';
import { Alert, fmtDate, Spinner } from '../lib/ui.js';
import { DateField } from '../lib/DateField.js';
import { formatRupees, localYmd } from '@siva/shared';
import { t } from '../i18n/index.js';

type Fee = {
  id: string;
  effective_from: string;
  monthly_fee_rupees: number;
  note: string | null;
};

/**
 * What a worker is paid, as a history.
 *
 * Admin-only and audited on both reading and writing — this card is the only place the figure
 * appears in the whole application, and it is deliberately not on the worker list or in any
 * report. A rate is never edited: a change is a new row from a date, so a document issued in
 * March still says what March was worth after April's rise.
 */
export function FeeCard({ workerId }: { workerId: string }) {
  const [fees, setFees] = useState<Fee[] | null>(null);
  const [tdsRateBp, setTdsRateBp] = useState(100);
  const [from, setFrom] = useState(localYmd(new Date()));
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api<{ fees: Fee[]; tds_rate_bp: number }>(`/admin/workers/${workerId}/fees`);
      setFees(r.fees);
      setTdsRateBp(r.tds_rate_bp);
      setError(null);
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [workerId]);
  useEffect(() => {
    void load();
  }, [load]);

  const add = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await post(`/admin/workers/${workerId}/fees`, {
        effective_from: from,
        monthly_fee_rupees: Number(amount),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      setAmount('');
      setNote('');
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (fee: Fee) => {
    setBusy(true);
    setError(null);
    try {
      await del(`/admin/workers/${workerId}/fees/${fee.id}`);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <h3>{t('fee_title')}</h3>
      <p className="muted">{t('fee_sub')}</p>
      {error && <Alert>{error}</Alert>}

      {!fees ? (
        <Spinner />
      ) : fees.length === 0 ? (
        <Alert kind="warn">{t('fee_none')}</Alert>
      ) : (
        <ul className="list">
          {fees.map((f) => (
            <li key={f.id} className="row between">
              <span>
                <strong>{formatRupees(f.monthly_fee_rupees)}</strong>{' '}
                <span className="muted">
                  {t('fee_from')} {fmtDate(f.effective_from, false)}
                </span>
                {f.note && (
                  <>
                    <br />
                    <small className="muted">{f.note}</small>
                  </>
                )}
              </span>
              <button
                type="button"
                className="small ghost"
                disabled={busy}
                onClick={() => void remove(f)}
              >
                {t('delete')}
              </button>
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={add} className="grid-form">
        {/* Wrapped in a label like the fields beside it. Bare, the date field had no caption
            line and sat a caption's height lower than Monthly Salary and Note. */}
        <label>
          {t('fee_from')}
          <DateField value={from} onChange={setFrom} />
        </label>
        <label>
          {t('fee_amount')}
          <input
            type="number"
            inputMode="numeric"
            min={1}
            step={1}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
            placeholder="30000"
          />
        </label>
        <label>
          {t('fee_note')}
          <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} />
        </label>
        {/* In the grid, on the fields' baseline, not on a row of its own underneath the date
            with nothing beside it — which is how it read. */}
        <button type="submit" className="grid-form-action" disabled={busy || !amount}>
          {t('fee_add')}
        </button>
      </form>

      <p className="muted">
        <strong>
          {t('fee_tds')} — {(tdsRateBp / 100).toFixed(tdsRateBp % 100 === 0 ? 0 : 2)}%
        </strong>
        <br />
        {t('fee_tds_note')}
      </p>
    </section>
  );
}
